import "./styles.css";
import { playDrawSound, playHitSound, playShotSound, primeAudio } from "./app/audio";
import { detectQuality } from "./app/performance";
import {
  impact,
  initTelegram,
  setTelegramBackHandler,
  successHaptic,
  telegramInitData,
} from "./app/telegram";
import { GameWorld } from "./three/GameWorld";

function required<T extends Element>(value: T | null, name: string): T {
  if (!value) throw new Error("ui_missing:" + name);
  return value;
}

function clamp(value: number): number {
  return Math.max(-1, Math.min(1, value));
}

initTelegram();

const app = required(document.querySelector<HTMLDivElement>("#app"), "app");
app.innerHTML = [
  '<main class="app-shell" data-mode="lobby">',
  '<section class="hero-stage">',
  '<div class="canvas-host" id="canvas-host"></div>',
  '<div class="stage-shade"></div><div class="stage-grid"></div>',

  '<div id="hunt-aim" class="hunt-aim" tabindex="0" role="application" hidden aria-label="Creature Hunt. Hold and drag to aim. Release to shoot.">',
  '<div class="hunt-vignette"></div>',
  '<div id="hunt-reticle" class="hunt-reticle"><i></i><i></i><b></b></div>',

  '<div class="hunt-top-hud">',
  '<button id="back" class="hunt-back" type="button" aria-label="Back to game lobby">←</button>',
  '<div class="hud-stat"><small>SCORE</small><b id="score">0</b></div>',
  '<div class="hud-stat"><small>TIME</small><b id="timer">45</b></div>',
  '<div class="hud-stat"><small>COMBO</small><b id="combo">x0</b></div>',
  '</div>',

  '<div id="shot-feedback" class="shot-feedback" data-kind="ready">FIND A TARGET</div>',

  '<div class="hunt-controls" aria-label="Hunt controls">',
  '<button id="scope-control" class="hunt-control scope-control" type="button" aria-label="Hold and drag to aim"><span>⌖</span><small>AIM</small></button>',
  '<button id="fire-control" class="hunt-control fire-control" type="button" aria-label="Hold to draw, release to fire"><span>➤</span><small>FIRE</small></button>',
  '</div>',

  '<div class="hunt-bottom-hud">',
  '<div class="hunt-info-row"><span>HITS <b id="hits">0</b></span><span>SHOTS <b id="shots">0</b></span></div>',
  '<div class="power-row"><span>DRAW POWER</span><b id="power-label">45%</b></div>',
  '<div class="power"><i id="power-fill"></i></div>',
  '<div class="aim-hint">LEFT AIM · RIGHT FIRE</div>',
  '</div>',

  '<div id="round-result" class="round-result" hidden>',
  '<div class="round-card">',
  '<small>HUNT COMPLETE</small>',
  '<h2 id="result-score">0</h2>',
  '<p>FINAL SCORE</p>',
  '<div class="result-grid">',
  '<div><small>HITS</small><b id="result-hits">0</b></div>',
  '<div><small>SHOTS</small><b id="result-shots">0</b></div>',
  '<div><small>BEST COMBO</small><b id="result-combo">x0</b></div>',
  '</div>',
  '<button id="replay" class="result-primary" type="button">HUNT AGAIN</button>',
  '<button id="result-lobby" class="result-secondary" type="button">BACK TO LOBBY</button>',
  '</div>',
  '</div>',
  '</div>',

  '<div id="dice-game" class="dice-game" hidden>',
  '<div class="dice-vignette"></div>',
  '<div class="dice-top-hud">',
  '<button id="dice-back" class="dice-back" type="button" aria-label="Back to game lobby">←</button>',
  '<div class="dice-score"><small>YOU</small><b id="dice-player-score">0</b></div>',
  '<div class="dice-round"><small>DUEL</small><b id="dice-roll-count">0</b></div>',
  '<div class="dice-score"><small>RIVAL</small><b id="dice-rival-score">0</b></div>',
  '</div>',
  '<div id="dice-status" class="dice-status" data-kind="ready">',
  '<small id="dice-kicker">READY</small>',
  '<strong id="dice-result">ROLL TO DUEL</strong>',
  '<p id="dice-values">YOU — · — RIVAL</p>',
  '</div>',
  '<div id="dice-gesture" class="dice-gesture" role="button" tabindex="0" aria-label="Tap or swipe up to roll the dice"><span>↑</span><small>SWIPE UP</small></div>',
  '<div class="dice-bottom">',
  '<button id="dice-roll" class="dice-roll" type="button"><span>ROLL DICE</span><b>🎲</b></button>',
  '<p>Tap ROLL or swipe up · higher die wins</p>',
  '<div class="dice-streak"><span>STREAK</span><b id="dice-streak">x0</b></div>',
  '</div>',
  '</div>',

  '<header class="topbar"><div class="brand"><span>M</span><div><small>MRK PLAY</small><b>GAME HUB</b></div></div><div class="live"><i></i> LIVE</div></header>',
  '<div class="hero-copy"><small>REAL-TIME 3D WILDLIFE CHASE</small><h1>FOREST <em>HUNT</em></h1><p>Run · Track · Draw · Release</p></div>',
  '<div class="quality" id="quality"></div>',
  '</section>',

  '<section class="panel">',
  '<div id="lobby">',
  '<div class="meta"><div><small>WILDLIFE</small><b>HARE</b></div><div><small>ROUND</small><b>45 SECONDS</b></div><div><small>MODE</small><b>CHASE</b></div></div>',
  '<button id="enter" class="primary" type="button"><span><small>PROFESSIONAL PREVIEW</small>START FOREST HUNT</span><b>→</b></button>',
  '<div class="hunt-types"><span>RUN FORWARD</span><span>TRACK HARE</span><span>FIRE ARROW</span></div>',
  '<div class="future"><button id="dice-entry" class="game-card active-game" type="button"><span>🎲</span><div><b>Dice Duel</b><small>Play now · 3D</small></div><i>→</i></button><article><span>👥</span><div><b>Team Clash</b><small>Coming next</small></div></article></div>',
  '</div>',
  '</section>',
  '</main>',
].join("");

const host = required(document.querySelector<HTMLElement>("#canvas-host"), "canvas-host");
const shell = required(document.querySelector<HTMLElement>(".app-shell"), "app-shell");
const lobby = required(document.querySelector<HTMLElement>("#lobby"), "lobby");
const huntAim = required(document.querySelector<HTMLElement>("#hunt-aim"), "hunt-aim");
const enter = required(document.querySelector<HTMLButtonElement>("#enter"), "enter");
const enterCopy = required(enter.querySelector<HTMLSpanElement>("span"), "enter-copy");
const back = required(document.querySelector<HTMLButtonElement>("#back"), "back");
const replay = required(document.querySelector<HTMLButtonElement>("#replay"), "replay");
const resultLobby = required(document.querySelector<HTMLButtonElement>("#result-lobby"), "result-lobby");
const reticle = required(document.querySelector<HTMLElement>("#hunt-reticle"), "hunt-reticle");
const scopeControl = required(document.querySelector<HTMLButtonElement>("#scope-control"), "scope-control");
const fireControl = required(document.querySelector<HTMLButtonElement>("#fire-control"), "fire-control");
const score = required(document.querySelector<HTMLElement>("#score"), "score");
const timer = required(document.querySelector<HTMLElement>("#timer"), "timer");
const comboLabel = required(document.querySelector<HTMLElement>("#combo"), "combo");
const hits = required(document.querySelector<HTMLElement>("#hits"), "hits");
const shots = required(document.querySelector<HTMLElement>("#shots"), "shots");
const qualityLabel = required(document.querySelector<HTMLElement>("#quality"), "quality");
const feedback = required(document.querySelector<HTMLElement>("#shot-feedback"), "shot-feedback");
const powerFill = required(document.querySelector<HTMLElement>("#power-fill"), "power-fill");
const powerLabel = required(document.querySelector<HTMLElement>("#power-label"), "power-label");
const roundResult = required(document.querySelector<HTMLElement>("#round-result"), "round-result");
const resultScore = required(document.querySelector<HTMLElement>("#result-score"), "result-score");
const resultHits = required(document.querySelector<HTMLElement>("#result-hits"), "result-hits");
const resultShots = required(document.querySelector<HTMLElement>("#result-shots"), "result-shots");
const resultCombo = required(document.querySelector<HTMLElement>("#result-combo"), "result-combo");

const diceGame = required(document.querySelector<HTMLElement>("#dice-game"), "dice-game");
const diceEntry = required(document.querySelector<HTMLButtonElement>("#dice-entry"), "dice-entry");
const diceBack = required(document.querySelector<HTMLButtonElement>("#dice-back"), "dice-back");
const diceRoll = required(document.querySelector<HTMLButtonElement>("#dice-roll"), "dice-roll");
const diceGesture = required(document.querySelector<HTMLElement>("#dice-gesture"), "dice-gesture");
const dicePlayerScoreLabel = required(document.querySelector<HTMLElement>("#dice-player-score"), "dice-player-score");
const diceRivalScoreLabel = required(document.querySelector<HTMLElement>("#dice-rival-score"), "dice-rival-score");
const diceRollCountLabel = required(document.querySelector<HTMLElement>("#dice-roll-count"), "dice-roll-count");
const diceStreakLabel = required(document.querySelector<HTMLElement>("#dice-streak"), "dice-streak");
const diceStatus = required(document.querySelector<HTMLElement>("#dice-status"), "dice-status");
const diceKicker = required(document.querySelector<HTMLElement>("#dice-kicker"), "dice-kicker");
const diceResult = required(document.querySelector<HTMLElement>("#dice-result"), "dice-result");
const diceValues = required(document.querySelector<HTMLElement>("#dice-values"), "dice-values");

const quality = detectQuality();
qualityLabel.textContent = quality.tier.toUpperCase();

let total = 0;
let shotCount = 0;
let hitCount = 0;
let combo = 0;
let bestCombo = 0;
let aimX = 0;
let aimY = 0;
let aiming = false;
let playing = false;
let roundActive = false;
let shotLocked = false;
let chargeStarted = 0;
let chargeFrame = 0;
let timerFrame = 0;
let feedbackTimer = 0;
let weaponUnlockTimer = 0;
let shotPower = 0.45;
let roundEndsAt = 0;
let lastPointerX = 0;
let lastPointerY = 0;
let preparingHunt = false;
let scoped = false;
let scenePointerId: number | null = null;
let scopePointerId: number | null = null;
let firePointerId: number | null = null;

let dicePlayerScore = 0;
let diceRivalScore = 0;
let diceRollCount = 0;
let diceStreak = 0;
let diceRolling = false;
let diceGesturePointerId: number | null = null;
let diceGestureStartY = 0;

function renderStats(): void {
  score.textContent = String(total);
  shots.textContent = String(shotCount);
  hits.textContent = String(hitCount);
  comboLabel.textContent = "x" + String(combo);
  comboLabel.dataset.hot = combo >= 3 ? "true" : "false";
}

function showFeedback(
  text: string,
  kind: "critical" | "hit" | "miss" | "ready" = "ready",
): void {
  window.clearTimeout(feedbackTimer);

  feedback.textContent = text;
  feedback.dataset.kind = kind;
  feedback.classList.remove("pop");
  void feedback.offsetWidth;
  feedback.classList.add("pop");

  if (kind !== "ready") {
    feedbackTimer = window.setTimeout(() => {
      if (!roundActive) return;
      feedback.textContent = "TRACK A CREATURE";
      feedback.dataset.kind = "ready";
    }, 850);
  }
}

function renderDiceStats(): void {
  dicePlayerScoreLabel.textContent = String(dicePlayerScore);
  diceRivalScoreLabel.textContent = String(diceRivalScore);
  diceRollCountLabel.textContent = String(diceRollCount);
  diceStreakLabel.textContent = "x" + String(diceStreak);
  diceStreakLabel.dataset.hot = diceStreak >= 3 ? "true" : "false";
}

function setDiceStatus(
  kicker: string,
  result: string,
  values: string,
  kind: "ready" | "rolling" | "win" | "draw" | "lose",
): void {
  diceKicker.textContent = kicker;
  diceResult.textContent = result;
  diceValues.textContent = values;
  diceStatus.dataset.kind = kind;
  diceStatus.classList.remove("pop");
  void diceStatus.offsetWidth;
  diceStatus.classList.add("pop");
}

const world = new GameWorld(host, quality, (result) => {
  if (!roundActive) return;

  if (result.hit) {
    hitCount += 1;
    combo += 1;
    bestCombo = Math.max(bestCombo, combo);

    const comboBonus = Math.min(Math.max(combo - 1, 0), 5);
    const points = result.score + comboBonus;
    total += points;

    playHitSound(Math.min(10, result.score));

    if (result.label === "CRITICAL") {
      successHaptic();
      showFeedback("CRITICAL  +" + String(points), "critical");
    } else {
      impact("medium");
      showFeedback("HIT  +" + String(points), "hit");
    }
  } else {
    combo = 0;
    playHitSound(0);
    impact("soft");
    showFeedback("MISS · COMBO LOST", "miss");
  }

  renderStats();

  reticle.classList.remove("hit");
  void reticle.offsetWidth;
  reticle.classList.add("hit");
}, (result) => {
  if (shell.dataset.mode !== "dice") return;

  diceRolling = false;
  diceRoll.disabled = false;
  diceRoll.classList.remove("rolling");
  diceRollCount += 1;

  if (result.outcome === "win") {
    dicePlayerScore += 1;
    diceStreak += 1;
    successHaptic();
    playHitSound(10);
    setDiceStatus(
      "ROUND " + String(diceRollCount),
      "YOU WIN",
      "YOU " + String(result.player) + " · " + String(result.rival) + " RIVAL",
      "win",
    );
  } else if (result.outcome === "lose") {
    diceRivalScore += 1;
    diceStreak = 0;
    impact("soft");
    playHitSound(0);
    setDiceStatus(
      "ROUND " + String(diceRollCount),
      "RIVAL WINS",
      "YOU " + String(result.player) + " · " + String(result.rival) + " RIVAL",
      "lose",
    );
  } else {
    diceStreak = 0;
    impact("medium");
    playHitSound(6);
    setDiceStatus(
      "ROUND " + String(diceRollCount),
      "DRAW",
      "YOU " + String(result.player) + " · " + String(result.rival) + " RIVAL",
      "draw",
    );
  }

  renderDiceStats();
});

function resetWeaponLock(locked = false): void {
  window.clearTimeout(weaponUnlockTimer);
  weaponUnlockTimer = 0;
  shotLocked = locked;
}

function lockWeaponForReload(): void {
  resetWeaponLock(true);
  weaponUnlockTimer = window.setTimeout(() => {
    weaponUnlockTimer = 0;
    if (roundActive) shotLocked = false;
  }, 220);
}

function resetAim(): void {
  aimX = 0;
  aimY = 0;
  world.aim(0, 0);
}

function setScoped(value: boolean): void {
  scoped = value && roundActive;
  huntAim.classList.toggle("scoped", scoped);
  scopeControl.classList.toggle("active", scoped);
  world.setScoped(scoped);
}

function mode(value: "lobby" | "hunt" | "dice"): void {
  const huntMode = value === "hunt";
  const diceMode = value === "dice";
  playing = value !== "lobby";

  shell.dataset.mode = value;
  lobby.hidden = playing;
  huntAim.hidden = !huntMode;
  diceGame.hidden = !diceMode;
  roundResult.hidden = true;

  cancelAnimationFrame(timerFrame);
  stopCharge(true);
  setScoped(false);
  roundActive = false;
  resetWeaponLock(false);

  world.setMode(value);
  impact(playing ? "medium" : "light");
  setTelegramBackHandler(playing ? () => mode("lobby") : null);

  if (huntMode) {
    startRound(true);
    window.setTimeout(() => huntAim.focus({ preventScroll: true }), 40);
    return;
  }

  if (diceMode) {
    startDiceSession();
    window.setTimeout(() => diceGesture.focus({ preventScroll: true }), 40);
    return;
  }

  resetAim();
}

function startDiceSession(): void {
  dicePlayerScore = 0;
  diceRivalScore = 0;
  diceRollCount = 0;
  diceStreak = 0;
  diceRolling = false;
  diceRoll.disabled = false;
  diceRoll.classList.remove("rolling");

  world.resetDice();
  renderDiceStats();
  setDiceStatus("READY", "ROLL TO DUEL", "YOU — · — RIVAL", "ready");
}

function rollDiceNow(): void {
  if (shell.dataset.mode !== "dice" || diceRolling) return;

  primeAudio();

  if (!world.rollDice()) return;

  diceRolling = true;
  diceRoll.disabled = true;
  diceRoll.classList.add("rolling");
  setDiceStatus(
    "ROLLING",
    "GOOD LUCK",
    "YOU ? · ? RIVAL",
    "rolling",
  );
  impact("rigid");
}

function startRound(resetWorld: boolean): void {
  total = 0;
  shotCount = 0;
  hitCount = 0;
  combo = 0;
  bestCombo = 0;
  resetWeaponLock(false);
  roundActive = true;
  setScoped(false);
  roundEndsAt = performance.now() + 45_000;
  roundResult.hidden = true;

  if (resetWorld) world.resetRound();

  resetAim();
  resetPower();
  renderStats();
  timer.textContent = "45";
  timer.dataset.low = "false";
  showFeedback("TRACK A CREATURE", "ready");

  cancelAnimationFrame(timerFrame);
  timerFrame = requestAnimationFrame(updateTimer);
}

function updateTimer(now: number): void {
  if (!roundActive) return;

  const remaining = Math.max(0, roundEndsAt - now);
  const seconds = Math.ceil(remaining / 1000);

  timer.textContent = String(seconds);
  timer.dataset.low = seconds <= 10 ? "true" : "false";

  if (remaining <= 0) {
    finishRound();
    return;
  }

  timerFrame = requestAnimationFrame(updateTimer);
}

function finishRound(): void {
  roundActive = false;
  resetWeaponLock(true);
  stopCharge(true);
  setScoped(false);

  resultScore.textContent = String(total);
  resultHits.textContent = String(hitCount);
  resultShots.textContent = String(shotCount);
  resultCombo.textContent = "x" + String(bestCombo);

  roundResult.hidden = false;
  showFeedback("ROUND COMPLETE", "ready");
  successHaptic();
}

function setAim(x: number, y: number): void {
  aimX = clamp(x);
  aimY = clamp(y);
  world.aim(aimX, aimY);
}

function moveAim(event: PointerEvent): void {
  const rect = huntAim.getBoundingClientRect();
  const dx = event.clientX - lastPointerX;
  const dy = event.clientY - lastPointerY;

  lastPointerX = event.clientX;
  lastPointerY = event.clientY;

  const sensitivityX = 3.4 / Math.max(1, rect.width);
  const sensitivityY = 3.0 / Math.max(1, rect.height);

  setAim(
    aimX + dx * sensitivityX,
    aimY - dy * sensitivityY,
  );
}

function renderCharge(): void {
  if (!aiming) return;

  const elapsed = Math.max(0, performance.now() - chargeStarted);
  const normalized = Math.min(1, elapsed / 820);
  const eased = 1 - Math.pow(1 - normalized, 2);

  shotPower = 0.45 + eased * 0.55;
  const pct = Math.round(shotPower * 100);

  powerFill.style.setProperty("--power", String(pct) + "%");
  powerLabel.textContent = String(pct) + "%";
  world.setDrawPower(shotPower);

  chargeFrame = requestAnimationFrame(renderCharge);
}

function startCharge(): void {
  if (!roundActive || shotLocked || aiming) return;

  aiming = true;
  chargeStarted = performance.now();
  shotPower = 0.45;

  reticle.classList.add("charging");
  huntAim.classList.add("drawing");

  playDrawSound();
  impact("light");
  renderCharge();
}

function resetPower(): void {
  shotPower = 0.45;
  powerFill.style.setProperty("--power", "45%");
  powerLabel.textContent = "45%";
  world.setDrawPower(0);
}

function stopCharge(cancelled: boolean): void {
  if (!aiming && !cancelled) return;

  aiming = false;
  cancelAnimationFrame(chargeFrame);
  reticle.classList.remove("charging");
  huntAim.classList.remove("drawing");

  if (cancelled) resetPower();
}

function releaseShot(): void {
  if (!aiming || !roundActive || shotLocked) return;

  const power = shotPower;
  stopCharge(false);

  // Weapon readiness is independent from the arrow's later hit/miss result.
  // This allows multiple arrows to be in flight without making FIRE feel stuck.
  lockWeaponForReload();
  shotCount += 1;
  renderStats();

  world.shoot(power);
  playShotSound();
  impact("rigid");
  resetPower();
}

enter.addEventListener("click", async () => {
  if (preparingHunt) return;

  primeAudio();
  preparingHunt = true;
  enter.disabled = true;
  enter.dataset.loading = "true";
  enterCopy.innerHTML = "<small>LOADING WILDLIFE + FOREST</small>PREPARING HUNT…";

  try {
    const ready = await world.prepareHunt();

    if (!ready) {
      enterCopy.innerHTML = "<small>ASSET NETWORK ERROR</small>TAP TO RETRY";
      showFeedback("REAL ASSETS COULD NOT LOAD", "miss");
      return;
    }

    enterCopy.innerHTML = "<small>PROFESSIONAL PREVIEW</small>START FOREST HUNT";
    mode("hunt");
  } finally {
    preparingHunt = false;
    enter.disabled = false;
    enter.dataset.loading = "false";
  }
});

back.addEventListener("click", () => mode("lobby"));
diceEntry.addEventListener("click", () => mode("dice"));
diceBack.addEventListener("click", () => mode("lobby"));
diceRoll.addEventListener("click", () => rollDiceNow());

diceGesture.addEventListener("pointerdown", (event) => {
  if (diceRolling) return;
  diceGesturePointerId = event.pointerId;
  diceGestureStartY = event.clientY;
  diceGesture.setPointerCapture(event.pointerId);
});

diceGesture.addEventListener("pointerup", (event) => {
  if (diceGesturePointerId !== event.pointerId) return;

  const deltaY = event.clientY - diceGestureStartY;
  diceGesturePointerId = null;

  if (deltaY <= -24 || Math.abs(deltaY) <= 10) {
    rollDiceNow();
  }
});

diceGesture.addEventListener("pointercancel", (event) => {
  if (diceGesturePointerId === event.pointerId) diceGesturePointerId = null;
});

diceGesture.addEventListener("keydown", (event) => {
  if (event.key !== " " && event.key !== "Enter" && event.key !== "ArrowUp") return;
  event.preventDefault();
  rollDiceNow();
});

replay.addEventListener("click", () => startRound(true));
resultLobby.addEventListener("click", () => mode("lobby"));

huntAim.addEventListener("pointerdown", (event) => {
  const target = event.target as HTMLElement;
  if (target.closest("button") || !roundActive) return;

  primeAudio();
  scenePointerId = event.pointerId;
  huntAim.setPointerCapture(event.pointerId);
  lastPointerX = event.clientX;
  lastPointerY = event.clientY;
});

huntAim.addEventListener("pointermove", (event) => {
  if (scenePointerId === event.pointerId && roundActive) moveAim(event);
});

huntAim.addEventListener("pointerup", (event) => {
  if (scenePointerId === event.pointerId) scenePointerId = null;
});

huntAim.addEventListener("pointercancel", (event) => {
  if (scenePointerId === event.pointerId) scenePointerId = null;
});

scopeControl.addEventListener("pointerdown", (event) => {
  if (!roundActive) return;

  event.preventDefault();
  event.stopPropagation();
  primeAudio();

  scopePointerId = event.pointerId;
  scopeControl.setPointerCapture(event.pointerId);
  lastPointerX = event.clientX;
  lastPointerY = event.clientY;

  setScoped(true);
  impact("light");
});

scopeControl.addEventListener("pointermove", (event) => {
  if (scopePointerId !== event.pointerId || !roundActive) return;
  event.preventDefault();
  moveAim(event);
});

const releaseScope = (event: PointerEvent): void => {
  if (scopePointerId !== event.pointerId) return;
  event.preventDefault();
  event.stopPropagation();
  scopePointerId = null;
  setScoped(false);
};

scopeControl.addEventListener("pointerup", releaseScope);
scopeControl.addEventListener("pointercancel", releaseScope);

fireControl.addEventListener("pointerdown", (event) => {
  if (!roundActive || shotLocked) return;

  event.preventDefault();
  event.stopPropagation();
  primeAudio();

  firePointerId = event.pointerId;
  fireControl.setPointerCapture(event.pointerId);
  fireControl.classList.add("active");
  startCharge();
});

const releaseFire = (event: PointerEvent): void => {
  if (firePointerId !== event.pointerId) return;

  event.preventDefault();
  event.stopPropagation();
  firePointerId = null;
  fireControl.classList.remove("active");
  releaseShot();
};

fireControl.addEventListener("pointerup", releaseFire);
fireControl.addEventListener("pointercancel", (event) => {
  if (firePointerId !== event.pointerId) return;

  event.preventDefault();
  event.stopPropagation();
  firePointerId = null;
  fireControl.classList.remove("active");
  stopCharge(true);
});

huntAim.addEventListener("keydown", (event) => {
  if (!roundActive || shotLocked) return;

  const step = event.shiftKey ? 0.12 : 0.06;

  if (event.key === "ArrowLeft") setAim(aimX - step, aimY);
  else if (event.key === "ArrowRight") setAim(aimX + step, aimY);
  else if (event.key === "ArrowUp") setAim(aimX, aimY + step);
  else if (event.key === "ArrowDown") setAim(aimX, aimY - step);
  else if (event.key === " " || event.key === "Enter") {
    if (!aiming) {
      primeAudio();
      startCharge();
    } else {
      releaseShot();
    }
  } else {
    return;
  }

  event.preventDefault();
});

setTelegramBackHandler(null);
resetPower();
renderStats();

if (telegramInitData()) shell.dataset.telegram = "connected";

window.addEventListener("pagehide", () => {
  cancelAnimationFrame(chargeFrame);
  cancelAnimationFrame(timerFrame);
  window.clearTimeout(feedbackTimer);
  window.clearTimeout(weaponUnlockTimer);
  setTelegramBackHandler(null);
  world.dispose();
}, { once: true });
